import "./MangoConfig.scss";

import React, { useEffect, useState } from "react";

import {
  Alert,
  Button,
  Card,
  Divider,
  Input,
  message,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
} from "antd";

import {
  CopyOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import {
  fetchMangoConfig,
  fetchSaleById,
  fetchSales,
  updateMangoConfig,
} from "../api";

const { Text } = Typography;

const MangoConfig = () => {
  const AVAILABLE_FEATURES = ["AI_VERSE", "OTO_ENTERPRISE"];

  const [loader, setLoader] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [editingSectionType, setEditingSectionType] = useState(null);
  const [editingTaggedSale, setEditingTaggedSale] = useState("");
  const [editingFeatures, setEditingFeatures] = useState([]);
  const [salesList, setSalesList] = useState([]);
  const [salesPage, setSalesPage] = useState(1);
  const [salesLoading, setSalesLoading] = useState(false);
  const [hasMoreSales, setHasMoreSales] = useState(true);

  // State for editing sale on configured rows
  const [editingSaleKey, setEditingSaleKey] = useState(null);
  const [editingSaleValue, setEditingSaleValue] = useState("");
  const [originalSaleValue, setOriginalSaleValue] = useState("");

  // State for editing features on configured feature rows
  const [editingConfiguredFeaturesKey, setEditingConfiguredFeaturesKey] =
    useState(null);
  const [editingConfiguredFeatures, setEditingConfiguredFeatures] = useState(
    []
  );
  const [originalConfiguredFeatures, setOriginalConfiguredFeatures] = useState(
    []
  );

  // State for expired/unavailable sales (sale ID -> sale data)
  const [expiredSalesMap, setExpiredSalesMap] = useState({});

  // Fetch active and upcoming sales for dropdown with pagination
  const loadSalesList = async (page = 1, append = false) => {
    if (salesLoading) return;

    try {
      setSalesLoading(true);
      const response = await fetchSales(page, 100000, "desc", [
        "active",
        "upcoming",
      ]);

      if (response?.success && response?.result?.sales) {
        const formattedSales = response.result.sales.map((sale) => ({
          value: sale._id,
          label: sale.title,
        }));

        if (append) {
          setSalesList((prev) => [...prev, ...formattedSales]);
        } else {
          setSalesList(formattedSales);
        }

        // Check if there are more sales to load
        const totalSales = response.result.total || 0;
        const loadedCount = append
          ? salesList.length + formattedSales.length
          : formattedSales.length;
        setHasMoreSales(loadedCount < totalSales);
        setSalesPage(page);
      }
    } catch (error) {
      console.error("Error fetching sales:", error);
    } finally {
      setSalesLoading(false);
    }
  };

  // Handle dropdown scroll for pagination
  const handleSalesDropdownScroll = (e) => {
    const { target } = e;
    if (
      target.scrollTop + target.offsetHeight >= target.scrollHeight - 10 &&
      hasMoreSales &&
      !salesLoading
    ) {
      loadSalesList(salesPage + 1, true);
    }
  };

  // Base structure with all plan combinations
  const getInitialConfigData = () => [
    {
      type: "FEATURE CONFIGURATION",
      mangoes: [1, 2, 3, 4, 5].map((index) => ({
        key: `feature-${index}`,
        toPlan: `Feature ${index}`,
        mangoLink: "-",
        basePrice: "-",
        status: "Not Configured",
        rawData: null,
      })),
    },
    {
      type: "PRICE BLOCK CONFIGURATION",
      mangoes: [1, 2, 3, 4, 5].map((index) => ({
        key: `price-block-${index}`,
        toPlan: `Price Block ${index}`,
        mangoLink: "-",
        basePrice: "-",
        taggedSale: "",
        status: "Not Configured",
        sale: "",
        rawData: null,
      })),
    },
    {
      type: "FREE",
      mangoes: [
        {
          key: "free-1",
          toPlan: "Freedom Basic",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "free-2",
          toPlan: "Freedom Pro",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "free-3",
          toPlan: "Freedom Advanced",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "free-4",
          toPlan: "Enterprise Basic",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "free-5",
          toPlan: "Enterprise Pro",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "free-6",
          toPlan: "Enterprise Advanced",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "free-7",
          toPlan: "Enterprise Ultimate",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
      ],
    },
    {
      type: "FREEDOM BASIC",
      mangoes: [
        {
          key: "fb-1",
          toPlan: "Freedom Pro",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "fb-2",
          toPlan: "Freedom Advanced",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "fb-3",
          toPlan: "Enterprise Basic",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "fb-4",
          toPlan: "Enterprise Pro",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "fb-5",
          toPlan: "Enterprise Advanced",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "fb-6",
          toPlan: "Enterprise Ultimate",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
      ],
    },
    {
      type: "FREEDOM PRO",
      mangoes: [
        {
          key: "fp-1",
          toPlan: "Freedom Advanced",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "fp-2",
          toPlan: "Enterprise Basic",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "fp-3",
          toPlan: "Enterprise Pro",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "fp-4",
          toPlan: "Enterprise Advanced",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "fp-5",
          toPlan: "Enterprise Ultimate",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
      ],
    },
    {
      type: "FREEDOM ADVANCED",
      mangoes: [
        {
          key: "fa-1",
          toPlan: "Enterprise Basic",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "fa-2",
          toPlan: "Enterprise Pro",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "fa-3",
          toPlan: "Enterprise Advanced",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "fa-4",
          toPlan: "Enterprise Ultimate",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
      ],
    },
    {
      type: "ENTERPRISE BASIC",
      mangoes: [
        {
          key: "eb-1",
          toPlan: "Enterprise Pro",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "eb-2",
          toPlan: "Enterprise Advanced",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "eb-3",
          toPlan: "Enterprise Ultimate",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
      ],
    },
    {
      type: "ENTERPRISE PRO",
      mangoes: [
        {
          key: "ep-1",
          toPlan: "Enterprise Advanced",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
        {
          key: "ep-2",
          toPlan: "Enterprise Ultimate",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
      ],
    },
    {
      type: "ENTERPRISE ADVANCED",
      mangoes: [
        {
          key: "ea-1",
          toPlan: "Enterprise Ultimate",
          mangoLink: "-",
          basePrice: "-",
          status: "Not Configured",
          rawData: null,
        },
      ],
    },
  ];

  // Dynamic data structure with type and mangoes array
  const [configData, setConfigData] = useState(getInitialConfigData());

  // Merge API data with base structure
  const mergeApiDataWithBase = (apiData) => {
    // Start with base structure
    const baseData = getInitialConfigData();

    // Create a lookup map from API data with raw data
    const apiMap = {};
    const tierMapper = {
      tier1: "Basic",
      tier2: "Pro",
      tier3: "Advanced",
      tier4: "Ultimate",
    };

    // Feature configs: any item whose response carries a non-empty features
    // array. Detected before price_block/plan_change so those branches skip it.
    const isFeatureConfig = (item) =>
      Array.isArray(item.features) && item.features.length >= 1;

    const featureConfigs = apiData
      .filter(isFeatureConfig)
      .slice()
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    if (featureConfigs.length) {
      apiMap["FEATURE CONFIGURATION"] = {};
      featureConfigs.forEach((item, i) => {
        apiMap["FEATURE CONFIGURATION"][`Feature ${i + 1}`] = {
          mangoLink: item.mango?._id || "-",
          basePrice: item.mango?.inrAmount
            ? `₹${item.mango.inrAmount?.toFixed(2)}`
            : "-",
          status: item.mango ? "Configured" : "Not Configured",
          rawData: item,
        };
      });
    }

    // Price blocks: order the API items by createdAt ascending and fan out
    // into Price Block 1..5 (row N gets the Nth-oldest configured block).
    const priceBlocks = apiData
      .filter((item) => item.type === "price_block" && !isFeatureConfig(item))
      .slice()
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
    if (priceBlocks.length) {
      apiMap["PRICE BLOCK CONFIGURATION"] = {};
      priceBlocks.forEach((item, i) => {
        apiMap["PRICE BLOCK CONFIGURATION"][`Price Block ${i + 1}`] = {
          mangoLink: item.mango?._id || "-",
          basePrice: item.mango?.inrAmount
            ? `₹${item.mango.inrAmount?.toFixed(2)}`
            : "-",
          status: item.mango && item?.sale ? "Configured" : "Not Configured",
          taggedSale: item.sale || "",
          sale: item.sale?._id || "",
          rawData: item, // Store complete API item
        };
      });
    }

    apiData.forEach((item) => {
      if (item.type === "plan_change" && !isFeatureConfig(item)) {
        // Plan Change Configuration
        const fromTier = (tierMapper[item.fromTier] || "").toUpperCase() || "";
        const fromType =
          item.fromPlatformSetup === "tagmango"
            ? "FREE"
            : (item.fromPlatformSetup || "")?.toUpperCase();

        const toType =
          item.toPlatformSetup === "tagmango"
            ? "FREE"
            : (item.toPlatformSetup || "")?.toUpperCase() || "";
        const toTier = tierMapper[item.toTier] || "";

        const fromKey =
          fromType === "FREE" ? "FREE" : `${fromType} ${fromTier}`.trim();
        const toPlanKey =
          toType === "FREE"
            ? "Free"
            : `${
                toType.charAt(0).toUpperCase() + toType.slice(1).toLowerCase()
              } ${toTier}`.trim();

        if (!apiMap[fromKey]) {
          apiMap[fromKey] = {};
        }

        apiMap[fromKey][toPlanKey] = {
          mangoLink: item.mango?._id || "-",
          basePrice: item.mango?.inrAmount
            ? `₹${item.mango.inrAmount?.toFixed(2)}`
            : "-",
          status: item.mango ? "Configured" : "Not Configured",
          rawData: item, // Store complete API item
        };
      }
    });

    // Merge API data into base structure
    const mergedData = baseData.map((section) => ({
      ...section,
      mangoes: section.mangoes.map((mango) => {
        const sectionApiData = apiMap[section.type];
        if (sectionApiData && sectionApiData[mango.toPlan]) {
          const merged = {
            ...mango,
            ...sectionApiData[mango.toPlan],
          };

          return merged;
        }
        return mango;
      }),
    }));

    return mergedData;
  };

  // Fetch sale by ID if not in salesList (for expired/unavailable sales)
  const fetchExpiredSaleData = async (saleId) => {
    if (!saleId || expiredSalesMap[saleId]) return;

    try {
      const response = await fetchSaleById(saleId);
      if (response?.result) {
        setExpiredSalesMap((prev) => ({
          ...prev,
          [saleId]: {
            value: response.result._id,
            label: response.result.title,
            status: response.result.status,
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching expired sale:", error);
    }
  };

  // Fetch mango config data
  const fetchData = async () => {
    try {
      setLoader(true);
      const response = await fetchMangoConfig();

      if (response?.result) {
        const mergedData = mergeApiDataWithBase(response.result);
        setConfigData(mergedData);

        // Check every price block for a sale that might be expired.
        response.result
          .filter((item) => item.type === "price_block")
          .forEach((priceBlockConfig) => {
            if (priceBlockConfig?.sale?._id) {
              fetchExpiredSaleData(priceBlockConfig.sale._id);
            }
          });
      } else {
        // If no API data, show base structure with "Not Configured"
        setConfigData(getInitialConfigData());
      }
    } catch (error) {
      console.error("Error fetching mango config:", error);
      message.error("Failed to fetch mango configurations");
      // On error, show base structure
      setConfigData(getInitialConfigData());
    } finally {
      setLoader(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchData();
    loadSalesList();
  }, []);

  // const handleRefresh = () => {
  //   fetchData();
  // };

  const handleEdit = (record, sectionType) => {
    setEditingKey(record.key);
    setEditingValue("");
    setEditingTaggedSale(record.taggedSale || "");
    setEditingSectionType(sectionType);
    if (sectionType === "FEATURE CONFIGURATION") {
      setEditingFeatures(
        record.rawData?.features?.length
          ? [record.rawData.features[0]]
          : [AVAILABLE_FEATURES[0]]
      );
    } else {
      setEditingFeatures([]);
    }
  };

  // Helper function to parse platform setup and tier from section type or plan name
  const parsePlatformAndTier = (text) => {
    const normalized = text.toUpperCase().trim();

    // Map platform names
    let platformSetup = "";
    let tier = "FREE";

    if (normalized === "FREE" || normalized === "PRICE BLOCK") {
      platformSetup = "tagmango";
      tier = "tier1";
    } else if (normalized.includes("FREEDOM")) {
      platformSetup = "freedom";
      if (normalized.includes("BASIC")) {
        tier = "tier1";
      } else if (normalized.includes("PRO")) {
        tier = "tier2";
      } else if (normalized.includes("ADVANCED")) {
        tier = "tier3";
      }
    } else if (normalized.includes("ENTERPRISE")) {
      platformSetup = "enterprise";
      if (normalized.includes("BASIC")) {
        tier = "tier1";
      } else if (normalized.includes("PRO")) {
        tier = "tier2";
      } else if (normalized.includes("ADVANCED")) {
        tier = "tier3";
      } else if (normalized.includes("ULTIMATE")) {
        tier = "tier4";
      }
    }

    return { platformSetup, tier };
  };

  const handleSave = async (record, sectionType) => {
    try {
      setLoader(true);

      // Build API payload
      let payload = {};

      if (sectionType === "PRICE BLOCK CONFIGURATION") {
        // Reject when the Mango ID is already assigned to another Price Block.
        const trimmedMangoId = editingValue?.trim();
        const priceBlockSection = configData.find(
          (section) => section.type === "PRICE BLOCK CONFIGURATION"
        );
        const isDuplicate = priceBlockSection?.mangoes?.some(
          (row) =>
            row.key !== record.key &&
            row.mangoLink &&
            row.mangoLink !== "-" &&
            row.mangoLink === trimmedMangoId
        );
        if (isDuplicate) {
          message.error(
            "This Mango ID is already used in another Price Block. Please choose a different Mango."
          );
          setLoader(false);
          return;
        }

        payload = {
          type: "price_block",
          mango: editingValue,
        };

        // Add sale if selected
        if (editingTaggedSale) {
          payload.sale = editingTaggedSale;
        }
      } else if (sectionType === "FEATURE CONFIGURATION") {
        // Reject when the Mango ID is already assigned to another Feature row.
        const trimmedMangoId = editingValue?.trim();
        const featureSection = configData.find(
          (section) => section.type === "FEATURE CONFIGURATION"
        );
        const isDuplicate = featureSection?.mangoes?.some(
          (row) =>
            row.key !== record.key &&
            row.mangoLink &&
            row.mangoLink !== "-" &&
            row.mangoLink === trimmedMangoId
        );
        if (isDuplicate) {
          message.error(
            "This Mango ID is already used in another Feature configuration. Please choose a different Mango."
          );
          setLoader(false);
          return;
        }

        payload = {
          type: "feature",
          mango: editingValue,
          features: editingFeatures,
        };
      } else {
        // Plan change type - use rawData if available
        if (record.rawData) {
          // Updating existing configuration - use raw data from API
          payload = {
            type: "plan_change",
            mango: editingValue,
            fromPlatformSetup: record.rawData.fromPlatformSetup,
            fromTier: record.rawData.fromTier,
            toPlatformSetup: record.rawData.toPlatformSetup,
            toTier: record.rawData.toTier,
          };
        } else {
          // Creating new configuration - use parsing functio
          const { platformSetup: fromPlatformSetup, tier: fromTier } =
            parsePlatformAndTier(sectionType);
          const { platformSetup: toPlatformSetup, tier: toTier } =
            parsePlatformAndTier(record.toPlan);

          payload = {
            type: "plan_change",
            mango: editingValue,
            fromPlatformSetup,
            fromTier,
            toPlatformSetup,
            toTier,
          };
        }

        // Add sale if selected (optional)
        if (editingTaggedSale) {
          payload.sale = editingTaggedSale;
        }
      }

      // Call the API
      const response = await updateMangoConfig(payload);

      if (response?.success) {
        message.success("Mango configuration updated successfully");

        // Reset editing state
        setEditingKey(null);
        setEditingValue("");
        setEditingTaggedSale("");
        setEditingSectionType(null);

        // Refresh data from API
        await fetchData();
      } else {
        message.error(response?.message || "Failed to update configuration");
        setLoader(false);
      }
    } catch (error) {
      console.error("Error updating mango config:", error);
      message.error(error.message || error);
      setLoader(false);
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditingValue("");
    setEditingTaggedSale("");
    setEditingSectionType(null);
    setEditingFeatures([]);
  };

  // Handle sale change on configured rows
  const handleConfiguredSaleChange = (value, record) => {
    setEditingSaleKey(record.key);
    setEditingSaleValue(value);
    if (!originalSaleValue) {
      setOriginalSaleValue(
        record.taggedSale || record.rawData?.sale?._id || ""
      );
    }
  };

  // Cancel sale edit on configured rows
  const handleConfiguredSaleCancel = () => {
    setEditingSaleKey(null);
    setEditingSaleValue("");
    setOriginalSaleValue("");
  };

  // Handle features change on configured feature-config rows
  const handleConfiguredFeaturesChange = (nextValues, record) => {
    setEditingConfiguredFeaturesKey(record.key);
    setEditingConfiguredFeatures(nextValues);
    if (
      editingConfiguredFeaturesKey !== record.key ||
      !originalConfiguredFeatures.length
    ) {
      setOriginalConfiguredFeatures(record.rawData?.features || []);
    }
  };

  // Cancel feature edit on configured feature-config rows
  const handleConfiguredFeaturesCancel = () => {
    setEditingConfiguredFeaturesKey(null);
    setEditingConfiguredFeatures([]);
    setOriginalConfiguredFeatures([]);
  };

  // Save updated features on configured feature-config rows
  const handleConfiguredFeaturesSave = async (record) => {
    try {
      setLoader(true);

      const payload = {
        type: "feature",
        mango: record.rawData?.mango?._id || record.rawData?.mango,
        features: editingConfiguredFeatures,
      };

      const response = await updateMangoConfig(payload);

      if (response?.success) {
        message.success("Features updated successfully");
        handleConfiguredFeaturesCancel();
        fetchData();
      } else {
        message.error(response?.message || "Failed to update features");
      }
    } catch (error) {
      console.error("Error updating features:", error);
      message.error(error.message || error);
    } finally {
      setLoader(false);
    }
  };

  // Save updated sale on configured rows
  const handleConfiguredSaleSave = async (record) => {
    try {
      setLoader(true);

      const payload = {
        type: "price_block",
        mango: record.rawData?.mango?._id || record.rawData?.mango,
        sale: editingSaleValue,
      };

      const response = await updateMangoConfig(payload);

      if (response?.success) {
        message.success("Sale updated successfully");
        handleConfiguredSaleCancel();
        fetchData();
      } else {
        message.error(response?.message || "Failed to update sale");
      }
    } catch (error) {
      console.error("Error updating sale:", error);
      message.error(error.message || error);
    } finally {
      setLoader(false);
    }
  };

  const columns = (sectionType) => {
    const baseColumns = [
      {
        title: "TO PLAN",
        dataIndex: "toPlan",
        key: "toPlan",
        render: (text) => (
          <Typography.Text className="mango-config__column-text">
            {text}
          </Typography.Text>
        ),
      },
      {
        title: "MANGO ID",
        dataIndex: "mangoLink",
        key: "mangoLink",
        render: (text, record) => {
          const isEditing =
            editingKey === record.key && editingSectionType === sectionType;
          const mangoData = record.rawData?.mango;
          const isMangoDeleted = mangoData?.isDeleted;
          const isMangoHidden = mangoData?.isHidden;

          return isEditing ? (
            <Input
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              placeholder="Enter mango id"
              className="mango-config__input"
            />
          ) : (
            <>
              <Typography.Text>{text}</Typography.Text>

              {!!text && text !== "-" && (
                <Button
                  type="text"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${process.env.REACT_APP_TAGMANGO_CHECKOUT_URL}/${text}`
                    );
                    message.success("Link copied to clipboard");
                  }}
                  style={{ marginLeft: 8 }}
                />
              )}

              {!!text && text !== "-" && (isMangoDeleted || isMangoHidden) && (
                <Typography.Text
                  style={{
                    display: "block",
                    color: "#ef4444",
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                  {isMangoDeleted
                    ? "This Mango has been deleted"
                    : "This mango is currently hidden in your account"}
                </Typography.Text>
              )}
            </>
          );
        },
      },
      {
        title: (
          <>
            <Text>BASE PRICE (₹)</Text>
            <Tooltip title="The Base Price displays the Mango price excluding GST.">
              <InfoCircleOutlined style={{ marginLeft: 8 }} />
            </Tooltip>
          </>
        ),
        dataIndex: "basePrice",
        key: "basePrice",
      },
    ];

    // Add TAG A SALE column only for PRICE BLOCK CONFIGURATION
    if (sectionType === "PRICE BLOCK CONFIGURATION") {
      baseColumns.push({
        title: "TAG A SALE",
        dataIndex: "taggedSale",
        key: "taggedSale",
        render: (text, record) => {
          const isEditing =
            editingKey === record.key && editingSectionType === sectionType;
          const isConfigured = record.status === "Configured";
          const isEditingSale = editingSaleKey === record.key;

          // For configured rows - show dropdown with save/cancel when changed
          if (isConfigured && !isEditing) {
            const currentSaleId =
              record.rawData?.sale?._id || record.taggedSale || "";
            const displayValue = isEditingSale
              ? editingSaleValue
              : currentSaleId;
            const hasChanged =
              isEditingSale && editingSaleValue !== originalSaleValue;

            // Check if sale is in salesList (active/upcoming)
            const saleInList = salesList.find(
              (sale) => sale.value === displayValue
            );
            // Check if sale is in expiredSalesMap (expired/unavailable)
            const expiredSale = expiredSalesMap[displayValue];

            // If sale is not in salesList and not in expiredSalesMap, try to fetch it
            if (displayValue && !saleInList && !expiredSale) {
              fetchExpiredSaleData(displayValue);
            }

            // Build options: active/upcoming sales + expired sale (if any)
            const dropdownOptions = [...salesList];
            if (expiredSale && !saleInList) {
              // Add expired sale at the top as a disabled option (can see but not re-select)
              dropdownOptions.unshift({
                ...expiredSale,
                disabled: true,
                label: `${expiredSale.label} (${
                  expiredSale.status || "Expired"
                })`,
              });
            }

            return (
              <Space direction="vertical" size="small">
                <Select
                  value={displayValue || undefined}
                  onChange={(value) =>
                    handleConfiguredSaleChange(value, record)
                  }
                  placeholder="Select a sale"
                  className="mango-config__select"
                  size="large"
                  onPopupScroll={handleSalesDropdownScroll}
                  loading={salesLoading}
                  notFoundContent={
                    salesLoading ? "Loading..." : "No sales found"
                  }
                >
                  {dropdownOptions.map((sale) => (
                    <Select.Option
                      key={sale.value}
                      value={sale.value}
                      disabled={sale.disabled}
                    >
                      {sale.label}
                    </Select.Option>
                  ))}
                  {salesLoading && salesList.length > 0 && (
                    <Select.Option disabled key="loading">
                      Loading more...
                    </Select.Option>
                  )}
                </Select>
                {hasChanged && (
                  <Space>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleConfiguredSaleSave(record)}
                      className="mango-config__save-btn"
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      onClick={handleConfiguredSaleCancel}
                      className="mango-config__cancel-btn"
                    >
                      Cancel
                    </Button>
                  </Space>
                )}
              </Space>
            );
          }

          // For non-configured rows being edited
          const displayValue = isEditing
            ? editingTaggedSale
            : record.taggedSale || undefined;

          return (
            <Select
              value={displayValue}
              onChange={(value) => setEditingTaggedSale(value)}
              placeholder="Select a sale"
              className="mango-config__select"
              size="large"
              disabled={!isEditing}
              onPopupScroll={handleSalesDropdownScroll}
              loading={salesLoading}
              notFoundContent={salesLoading ? "Loading..." : "No sales found"}
            >
              {salesList.map((sale) => (
                <Select.Option key={sale.value} value={sale.value}>
                  {sale.label}
                </Select.Option>
              ))}
              {salesLoading && salesList.length > 0 && (
                <Select.Option disabled key="loading">
                  Loading more...
                </Select.Option>
              )}
            </Select>
          );
        },
      });
    }

    // Add FEATURES column only for FEATURE CONFIGURATION
    if (sectionType === "FEATURE CONFIGURATION") {
      baseColumns.push({
        title: "FEATURES",
        dataIndex: "features",
        key: "features",
        render: (_, record) => {
          const isEditing =
            editingKey === record.key && editingSectionType === sectionType;
          const isConfigured = record.status === "Configured";
          const isEditingConfigured =
            editingConfiguredFeaturesKey === record.key;
          const configuredFeatures = record.rawData?.features || [];

          // For configured rows - allow inline edit with Save/Cancel
          if (isConfigured && !isEditing) {
            const displayValue = isEditingConfigured
              ? editingConfiguredFeatures
              : configuredFeatures;
            const hasChanged =
              isEditingConfigured &&
              (editingConfiguredFeatures.length !==
                originalConfiguredFeatures.length ||
                editingConfiguredFeatures.some(
                  (f) => !originalConfiguredFeatures.includes(f)
                ));

            return (
              <Space direction="vertical" size="small">
                <Select
                  value={displayValue[0]}
                  onChange={(next) =>
                    handleConfiguredFeaturesChange(next ? [next] : [], record)
                  }
                  placeholder="Select feature"
                  className="mango-config__select"
                  size="large"
                  style={{ minWidth: 200 }}
                >
                  {AVAILABLE_FEATURES.map((f) => (
                    <Select.Option key={f} value={f}>
                      {f}
                    </Select.Option>
                  ))}
                </Select>
                {hasChanged && (
                  <Space>
                    <Button
                      type="primary"
                      size="small"
                      onClick={() => handleConfiguredFeaturesSave(record)}
                      className="mango-config__save-btn"
                      disabled={!editingConfiguredFeatures.length}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      onClick={handleConfiguredFeaturesCancel}
                      className="mango-config__cancel-btn"
                    >
                      Cancel
                    </Button>
                  </Space>
                )}
              </Space>
            );
          }

          // Configure-time (row being edited via the Configure button)
          const value = isEditing ? editingFeatures : configuredFeatures;
          return (
            <Select
              value={value[0]}
              onChange={(next) => setEditingFeatures(next ? [next] : [])}
              placeholder="Select feature"
              className="mango-config__select"
              size="large"
              disabled={!isEditing}
              style={{ minWidth: 200 }}
            >
              {AVAILABLE_FEATURES.map((f) => (
                <Select.Option key={f} value={f}>
                  {f}
                </Select.Option>
              ))}
            </Select>
          );
        },
      });
    }

    // Add remaining columns
    baseColumns.push({
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        const isEditing =
          editingKey === record.key && editingSectionType === sectionType;

        if (isEditing) {
          // Determine if Save button should be disabled
          const isPriceBlock = sectionType === "PRICE BLOCK CONFIGURATION";
          const isFeature = sectionType === "FEATURE CONFIGURATION";
          let isSaveDisabled;
          if (isPriceBlock) {
            isSaveDisabled = !editingValue || !editingTaggedSale;
          } else if (isFeature) {
            isSaveDisabled = !editingValue || !editingFeatures?.length;
          } else {
            isSaveDisabled = !editingValue;
          }

          // Show Save and Cancel buttons when editing
          return (
            <Space>
              <Button
                type="primary"
                onClick={() => handleSave(record, sectionType)}
                className="mango-config__save-btn"
                disabled={isSaveDisabled}
              >
                Save
              </Button>
              <Button
                onClick={handleCancel}
                className="mango-config__cancel-btn"
              >
                Cancel
              </Button>
            </Space>
          );
        }

        if (status === "Configured") {
          // Show "Configured" text if already configured
          return (
            <Typography.Text className="mango-config__configured-text">
              Configured
            </Typography.Text>
          );
        }

        // Show "Edit" button if not configured
        return (
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record, sectionType)}
            className="mango-config__edit-btn"
          >
            Configure
          </Button>
        );
      },
    });

    return baseColumns;
  };

  const renderConfigSection = (section, index) => {
    const isStandaloneSection =
      section.type === "PRICE BLOCK CONFIGURATION" ||
      section.type === "FEATURE CONFIGURATION";
    const sectionTitle = isStandaloneSection
      ? section.type
      : `FROM: ${section.type}`;
    const isLastSection = index === configData.length - 1;

    return (
      <div key={section.type}>
        {index > 0 && <Divider className="mango-config__section-divider" />}
        <Card
          className={`mango-config__card ${
            isLastSection
              ? "mango-config__card--last"
              : "mango-config__card--not-last"
          }`}
        >
          <Space
            direction="vertical"
            size="small"
            className="mango-config__section-header"
          >
            <Text className="mango-config__section-title">{sectionTitle}</Text>
          </Space>
          <Table
            columns={columns(section.type)}
            dataSource={section.mangoes}
            loading={loader}
            pagination={false}
            locale={{
              emptyText: "No configurations found",
            }}
            scroll={{ x: "auto" }}
          />
        </Card>
      </div>
    );
  };

  // Flatten all mangoes data for CSV export
  //   const getAllMangoesForExport = () => {
  //     return configData.flatMap((section) =>
  //       section.mangoes.map((mango) => ({
  //         fromPlan: section.type,
  //         ...mango,
  //       }))
  //     );
  //   };

  return (
    <>
      <div className="mango-config__header">
        <Typography.Title
          level={4}
          className="kyc-list__title mango-config__title"
        >
          Mango Configurations
        </Typography.Title>

        <Text type="secondary" className="mango-config__description">
          Mango ID mapping is a one-time configuration. Changes after setup are
          not supported through the platform and can only be made via a backend
          update in exceptional cases.
        </Text>
        <Alert
          className="mango-config__alert"
          message={
            <>
              <strong>Note:&nbsp;</strong>
              <span>
                Price Block is supported for both Open-Ended Mangoes (Pay What
                You Want) and One-Time (Paid) Mangoes. All remaining
                configurations are supported only for One-Time (Paid) Mangoes.
              </span>
            </>
          }
          type="info"
        />
      </div>
      <Divider />

      {configData.map((section, index) => renderConfigSection(section, index))}
    </>
  );
};

export default MangoConfig;
